import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  Box,
  Stack,
  IconButton,
  Drawer,
  Button,
  Container,
  Collapse,
} from "@mui/material";
import { useState, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { keyframes } from "@mui/system";
import { Link } from "react-router-dom"; // React Router Link
import "@fontsource/jaro";

const whiteButtonGlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(255, 255, 255, 0.8); }
  50% { box-shadow: 0 0 25px rgba(255, 255, 255, 0.8); }
  100% { box-shadow: 0 0 5px rgba(255, 255, 255, 0.8); }
`;

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const navItems = [
    { title: "Home", href: "/" },
    { title: "Blogs", href: "/blog" },
    { title: "About Us", href: "/about-us" },
    { title: "Services", href: "/services" },
    { title: "Teams", href: "/teams" },
  ];

  return (
    <Box
      sx={{
        position: "fixed",
        width: "100%",
        zIndex: 1100,
        top: isScrolled ? "10px" : "20px",
        transition: "all 0.3s ease",
        px: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="xl" disableGutters>
        <MuiAppBar
          position="static"
          sx={{
            backgroundColor: isScrolled ? "rgba(0, 0, 0, 0.95)" : "transparent",
            boxShadow: "none",
            borderRadius: "100px",
            py: { xs: 1, md: 0.5 },
            transition: "all 0.3s ease",
            backdropFilter: isScrolled ? "blur(10px)" : "none",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between" }}>
            {/* Logo - Left Side */}
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Jaro, sans-serif",
                color: "white",
                fontWeight: 400,
                fontSize: { xs: "1.8rem", md: "2.5rem" },
                flexShrink: 0,
              }}
            >
              Coding Forge
            </Typography>

            {/* Desktop Navigation - Center */}
            <Stack
              direction="row"
              spacing={4}
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                mx: 2,
              }}
            >
              {navItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.href}
                  style={{
                    color: "white",
                    textDecoration: "none",
                    fontSize: "1.2rem",
                    fontWeight: 500,
                    transition: "color 0.3s ease",
                  }}
                >
                  {item.title}
                </Link>
              ))}
            </Stack>

            {/* Book a Meeting Button - Right Side */}
            <Box sx={{ display: { xs: "none", md: "block" }, flexShrink: 0 }}>
              <Button
                variant="contained"
                disableElevation
                sx={{
                  backgroundColor: "white",
                  color: "black",
                  borderRadius: "50px",
                  px: 2.5,
                  py: 1,
                  fontSize: "15px",
                  fontWeight: 600,
                  animation: `${whiteButtonGlow} 2s infinite ease-in-out`,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                }}
              >
                Book a Meeting
              </Button>
            </Box>

            {/* Mobile Menu Icon */}
            <IconButton
              sx={{
                display: { xs: "flex", md: "none" },
                color: "white",
                zIndex: 1200,
              }}
              onClick={handleDrawerToggle}
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Toolbar>
        </MuiAppBar>

        {/* Mobile Menu - Collapsible */}
        <Collapse in={mobileOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.98)",
              color: "white",
              mt: 1,
              borderRadius: 0,
              padding: "20px",
              boxShadow: "none",
              display: { xs: "block", md: "none" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                "& a": {
                  width: "100%",
                  textAlign: "center",
                  padding: "12px 0",
                  textDecoration: "none",
                  color: "white",
                  borderRadius: 0,
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                },
              }}
            >
              {navItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.href}
                  onClick={handleDrawerToggle}
                  style={{
                    color: "white",
                    textDecoration: "none",
                    width: "100%",
                    textAlign: "center",
                    padding: "12px 0",
                    fontSize: "1.1rem",
                  }}
                >
                  {item.title}
                </Link>
              ))}
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "white",
                  color: "black",
                  "&:hover": {
                    backgroundColor: "#e0e0e0",
                  },
                  borderRadius: "50px",
                  padding: "10px 20px",
                  fontSize: "15px",
                  fontWeight: 600,
                  width: "160px",
                  animation: `${whiteButtonGlow} 2s infinite ease-in-out`,
                  mt: 2,
                  mb: 1,
                  boxShadow: "none",
                  textTransform: "none",
                }}
              >
                Book a Meeting
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Container>
    </Box>
  );
};

export default Navbar;
